// File: frontend/src/components/profile/ProfileAlbum.tsx

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { RiZoomInLine, RiImageLine } from "react-icons/ri";
import { EntryModel } from "../../models";
import { fetchProfileAlbum } from "../../services/profileService";
import { removeEntryImage } from "../../services/entryService";
import { useAuth } from "../../context/AuthContext";
import { collectEntryImageUrls } from "../entries/EntryImage";
import { resolveEntryImageSrc } from "../../utils/entryImageUrl";
import { ImageLightbox, ImageLightboxActions } from "../common/ImageLightbox";
import { SpinnerLoading, Pagination, generateMatchUrlFromEntry } from "../../utils";
import "./ProfileAlbum.css";

// Number of ENTRIES requested per backend call. The backend pages by entry
// (one entry can carry several images), so the album loads every image entry
// in chunks of this size and then pages by IMAGE on the client. That way every
// album page is filled completely instead of depending on how many images the
// entries of one backend page happen to contain.
const ALBUM_FETCH_CHUNK = 50;

// Safety cap on the number of chunk requests (ALBUM_FETCH_CHUNK * this value
// entries at most), so a broken totalPages value can never loop forever.
const ALBUM_MAX_FETCH_REQUESTS = 40;

// A page always shows at least this many full rows, even on very short screens
const ALBUM_MIN_ROWS = 2;

// Vertical space kept free for the site header, the profile tab strip, the
// count bar and the pagination when deciding how many rows fit on one screen
const ALBUM_RESERVED_HEIGHT = 240;

/**
 * Height of the visible screen used for the row calculation.
 *
 * documentElement.clientHeight is used instead of window.innerHeight: on
 * mobile browsers innerHeight changes every time the address bar collapses or
 * expands while scrolling, which would change the page size mid-scroll.
 */
function getViewportHeight(): number {
  return document.documentElement.clientHeight || window.innerHeight;
}

// Upper limit of rows per page outside the phone layout. On wide screens the
// grid has many columns, so filling the whole viewport height would put the
// entire album on a single page and hide the pagination.
const ALBUM_DESKTOP_MAX_ROWS = 3;

// Same breakpoint as the phone rule in ProfileAlbum.css (3 fixed columns).
// The phone layout keeps filling the screen height without the upper limit.
const ALBUM_PHONE_MAX_WIDTH = 560;

// Layout used until the grid has been measured once (first paint only)
const ALBUM_DEFAULT_COLUMNS = 6;
const ALBUM_DEFAULT_ROWS = 2;

interface ProfileAlbumProps {
  // Profile owner whose uploaded entry images are listed
  userId: number;
}

// One tile of the album: a single image plus the entry (and topic) it came from
interface AlbumItem {
  // Unique key — the same image id can legitimately appear in two entries
  key: string;
  // Entry the image belongs to — target of "entry'ye git" and "yayından kaldır"
  entryId: number;
  // Raw reference as written in the (gorsel: ...) token; the unpublish request
  // needs this, not the resolved src
  ref: string;
  // Resolved, ready-to-load image URL
  src: string;
  // Topic the image was posted under ("hangi başlık")
  title: string;
  // Link target of that topic page
  titleUrl: string;
}

/**
 * Resolve the topic title and topic URL of an entry.
 *
 * Mirrors the resolution ProfileEntryAccordion does for its header link: an
 * entry belongs to exactly one of person / club / team / custom topic / match,
 * checked in that priority order.
 */
function resolveEntryTopic(entry: EntryModel): { title: string; url: string } {
  if (entry.personId) {
    return { title: entry.personFullName ?? "—", url: `/kisi/${entry.personSlug}` };
  }
  if (entry.clubId) {
    return { title: entry.clubName ?? "—", url: `/kulup/${entry.clubSlug}` };
  }
  if (entry.teamId) {
    return { title: entry.teamName ?? "—", url: `/takim/${entry.clubSlug}/${entry.teamSlug}` };
  }
  if (entry.customTopicId) {
    return { title: entry.customTopicTitle ?? "—", url: `/gundem/${entry.customTopicSlug}` };
  }
  // Default: match entry
  return {
    title: `${entry.homeTeam} - ${entry.awayTeam}`,
    url: generateMatchUrlFromEntry(entry.homeTeam, entry.awayTeam, entry.matchDate, entry.matchId),
  };
}

/**
 * Flatten entries into one tile per image, keeping the order in which the
 * images were written inside their entry.
 */
function buildAlbumItems(entries: EntryModel[]): AlbumItem[] {
  const result: AlbumItem[] = [];
  // Entry ids already expanded — an entry returned by two backend pages must
  // not produce duplicate tiles (duplicate React keys drop tiles from the grid)
  const seenEntryIds = new Set<number>();
  entries.forEach((entry) => {
    if (seenEntryIds.has(entry.id)) return;
    seenEntryIds.add(entry.id);
    const topic = resolveEntryTopic(entry);
    collectEntryImageUrls(entry.content).forEach((ref, i) => {
      const src = resolveEntryImageSrc(ref);
      // A reference that does not resolve (removed upload, malformed token)
      // is skipped rather than rendered as a broken tile
      if (!src) return;
      result.push({
        key: `${entry.id}-${i}`,
        entryId: entry.id,
        ref,
        src,
        title: topic.title,
        titleUrl: topic.url,
      });
    });
  });
  return result;
}

/**
 * "Albüm" tab of the profile page.
 *
 * Lists every image the user has attached to their entries as a grid of
 * previews, each labelled with the topic it was posted under. Clicking a tile
 * opens the shared ImageLightbox with the whole page as its gallery, so the
 * arrow keys walk through the album instead of a single entry.
 *
 * The component owns its own data loading and pagination, exactly like the
 * follower / blocked lists do, so the profile page only has to mount it.
 *
 * Paging is done per IMAGE on the client: the page size is "columns that fit
 * the grid width" x "rows that fit the screen height", so every page except
 * the last one is completely filled.
 */
export const ProfileAlbum: React.FC<ProfileAlbumProps> = ({ userId }) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  // Only the album owner may unpublish images; everyone can open and download
  const isOwner = currentUser?.id === userId;

  // Every image of the album, across all backend pages
  const [allItems, setAllItems] = useState<AlbumItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Index (into allItems) of the first tile on the current page. Kept instead
  // of a page number so the same images stay in view when a resize changes
  // the page size.
  const [firstIndex, setFirstIndex] = useState(0);

  // Measured grid layout
  const [columns, setColumns] = useState(ALBUM_DEFAULT_COLUMNS);
  const [rows, setRows] = useState(ALBUM_DEFAULT_ROWS);
  const gridRef = useRef<HTMLDivElement>(null);

  // Index of the tile the lightbox is open on; null = closed
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      // Walk through all backend pages; the album is paged locally afterwards
      const entries: EntryModel[] = [];
      let pg = 1;
      let totalPages = 1;
      do {
        const res = await fetchProfileAlbum(userId, pg, ALBUM_FETCH_CHUNK);
        entries.push(...res.entries);
        totalPages = res.totalPages;
        pg += 1;
      } while (pg <= totalPages && pg <= ALBUM_MAX_FETCH_REQUESTS);

      setAllItems(buildAlbumItems(entries));
      setFirstIndex(0);
    } catch (err) {
      // Log the reason: a failed request and an empty album look identical otherwise
      console.error("Album could not be loaded:", err);
      setAllItems([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Measure how many columns the grid currently has (auto-fill resolves to a
   * concrete track list in the computed style) and how many rows of tiles fit
   * on the screen. Both values drive the page size.
   */
  const measure = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const style = window.getComputedStyle(grid);
    const trackCount = style.gridTemplateColumns
      .split(" ")
      .filter((track) => track.trim().length > 0).length;
    const nextColumns = Math.max(1, trackCount);

    const firstCard = grid.firstElementChild as HTMLElement | null;
    const rowGap = parseFloat(style.rowGap) || 0;
    const rowHeight = firstCard ? firstCard.offsetHeight + rowGap : 0;
    const nextRows = rowHeight > 0
      ? Math.max(ALBUM_MIN_ROWS, Math.floor((getViewportHeight() - ALBUM_RESERVED_HEIGHT) / rowHeight))
      : ALBUM_DEFAULT_ROWS;

    // Cap the rows outside the phone layout so wide screens still paginate
    const isPhone = window.innerWidth <= ALBUM_PHONE_MAX_WIDTH;
    setColumns(nextColumns);
    setRows(isPhone ? nextRows : Math.min(nextRows, ALBUM_DESKTOP_MAX_ROWS));
  }, []);

  // Re-measure whenever the grid width or the window height changes. The
  // setters bail out on identical values, so this cannot loop.
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    measure();

    const observer = new ResizeObserver(() => measure());
    observer.observe(grid);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, loading, allItems.length]);

  // Images per page: always whole rows that fill the visible area
  const pageSize = Math.max(1, columns * rows);
  const totalPages = Math.max(1, Math.ceil(allItems.length / pageSize));
  const page = Math.min(totalPages, Math.floor(firstIndex / pageSize) + 1);
  const pageStart = (page - 1) * pageSize;

  // Tiles of the current page
  const items = useMemo(
    () => allItems.slice(pageStart, pageStart + pageSize),
    [allItems, pageStart, pageSize]
  );

  // Gallery passed to the lightbox: every image of the current page, so the
  // prev/next controls move across the album and not just within one entry.
  const galleryUrls = useMemo(() => items.map((item) => item.src), [items]);

  /**
   * "yayından kaldır": remove the image from its entry, then drop every tile
   * showing the same reference of that entry (the backend removes all of its
   * tokens). The lightbox stays open and shows the image that slides into the
   * freed position. Errors are re-thrown so the lightbox can display them.
   */
  const handleUnpublish = useCallback(
    async (index: number) => {
      const item = items[index];
      if (!item) return;
      await removeEntryImage(item.entryId, item.ref);
      setAllItems((prev) =>
        prev.filter((it) => !(it.entryId === item.entryId && it.ref === item.ref))
      );
    },
    [items]
  );

  // Action bar of the lightbox. "entry'ye git" and download are available to
  // every visitor; "yayından kaldır" only to the owner of the album.
  const lightboxActions = useMemo<ImageLightboxActions>(
    () => ({
      entryIds: items.map((item) => item.entryId),
      onUnpublish: isOwner ? handleUnpublish : undefined,
      download: true,
    }),
    [items, isOwner, handleUnpublish]
  );

  const handlePageChange = (next: number) => {
    // Close the lightbox first: its index points into the page being replaced
    setLightboxIndex(null);
    const clamped = Math.min(Math.max(next, 1), totalPages);
    setFirstIndex((clamped - 1) * pageSize);
  };

  if (loading) {
    return (
      <div className="profile-entries-loading">
        <SpinnerLoading />
      </div>
    );
  }

  if (failed) {
    return (
      <div className="profile-entries-empty">
        <p>{t("profile_album_error")}</p>
      </div>
    );
  }

  if (allItems.length === 0) {
    return (
      <div className="profile-album-empty">
        <RiImageLine className="profile-album-empty__icon" aria-hidden="true" />
        <p>{t("profile_album_empty")}</p>
      </div>
    );
  }

  return (
    <div className="profile-album">
      {/* Count strip — how many images the whole album holds */}
      <div className="profile-album__bar">
        <span className="profile-album__count">
          {t("profile_album_count", { total: allItems.length })}
        </span>
      </div>

      <div className="profile-album__grid" ref={gridRef}>
        {items.map((item, index) => (
          <figure className="profile-album__card" key={item.key}>
            <button
              type="button"
              className="profile-album__thumb"
              onClick={() => setLightboxIndex(index)}
              title={item.title}
              aria-label={item.title}
            >
              <img
                src={item.src}
                alt=""
                className="profile-album__img"
                loading="lazy"
              />
              <span className="profile-album__overlay" aria-hidden="true">
                <RiZoomInLine />
              </span>
            </button>

            {/* Caption — the topic the image was posted under */}
            <figcaption className="profile-album__caption">
              <Link to={item.titleUrl} className="profile-album__title" title={item.title}>
                {item.title}
              </Link>
            </figcaption>
          </figure>
        ))}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={allItems.length}
          paginate={handlePageChange}
        />
      )}

      {lightboxIndex !== null && (
        <ImageLightbox
          images={galleryUrls}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          actions={lightboxActions}
        />
      )}
    </div>
  );
};

export default ProfileAlbum;